import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Separator } from "./components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { toast } from "sonner";
import {
  Heart, AlertTriangle, Users, Settings, Plus, Trash2, Save, RefreshCw,
  Link2, CheckCircle2, XCircle, ChevronRight, Search, ArrowUpDown,
  Phone, TrendingDown, UserX, BarChart3, Wifi, WifiOff, Shield,
  CalendarClock, FileText, Bell, Clock, Mail, ListTodo
} from "lucide-react";
import { CSMEmails } from "./CSMEmails";
import { TasksView, isOverdue, isDueSoon, useCsmUsers } from "./CSMTasks";
import { ClientTimeline } from "./CSMTimeline";
import { DigestSettings } from "./CSMDigest";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  onboarding: "bg-blue-100 text-blue-700 border-blue-200",
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  at_risk: "bg-orange-100 text-orange-700 border-orange-200",
  churn: "bg-red-100 text-red-700 border-red-200",
};
const STATUS_LABELS = { onboarding: "Onboarding", active: "Attivo", at_risk: "A Rischio", churn: "Churn" };

const FASCIA_COLORS = {
  green: { bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50", border: "border-emerald-200", label: "Verde" },
  yellow: { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50", border: "border-amber-200", label: "Giallo" },
  red: { bg: "bg-red-500", text: "text-red-700", light: "bg-red-50", border: "border-red-200", label: "Rosso" },
  none: { bg: "bg-gray-300", text: "text-gray-500", light: "bg-gray-50", border: "border-gray-200", label: "N/D" },
};

const SIGNAL_ICONS = {
  no_calls: Phone,
  usage_drop: TrendingDown,
  never_activated: UserX,
  score_drop: BarChart3,
  negative_emails: Mail,
};

// =============== HEALTH BADGE ===============
const HealthBadge = ({ score, fascia, size = "sm" }) => {
  const f = FASCIA_COLORS[fascia] || FASCIA_COLORS.none;
  if (score === null || score === undefined) {
    return <span className="text-xs text-muted-foreground italic">Non collegato</span>;
  }
  const sizeClasses = size === "lg" ? "w-12 h-12 text-lg" : "w-8 h-8 text-xs";
  return (
    <div className={`${sizeClasses} rounded-full ${f.bg} text-white font-bold flex items-center justify-center`} title={`Health Score: ${score} (${f.label})`}>
      {score}
    </div>
  );
};

// =============== CLIENT DETAIL DIALOG ===============
const ClientDetailDialog = ({ open, onClose, client, onSave }) => {
  const [contacts, setContacts] = useState([]);
  const [onboardingDate, setOnboardingDate] = useState("");
  const [clientStatus, setClientStatus] = useState("active");
  const [aicloOrgs, setAicloOrgs] = useState([]);
  const [csmNotes, setCsmNotes] = useState("");
  const [assignedCsm, setAssignedCsm] = useState("");
  const [saving, setSaving] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const csmUsers = useCsmUsers();

  useEffect(() => {
    if (client && open) {
      setContacts(client.contacts || []);
      setOnboardingDate(client.onboarding_date || "");
      setClientStatus(client.client_status || "active");
      setAicloOrgs(client.aiclo_orgs || []);
      setCsmNotes(client.csm_notes || "");
      setAssignedCsm(client.assigned_csm_id || "");
      // Load health data
      axios.get(`${API}/csm/health-scores/${client.client_id}`).then(res => {
        setHealthData(res.data);
      }).catch(() => setHealthData(null));
    }
  }, [client, open]);

  const addContact = () => setContacts([...contacts, { name: "", role: "", email: "" }]);
  const removeContact = (i) => setContacts(contacts.filter((_, idx) => idx !== i));
  const updateContact = (i, field, val) => {
    const updated = [...contacts];
    updated[i] = { ...updated[i], [field]: val };
    setContacts(updated);
  };

  const addOrg = () => setAicloOrgs([...aicloOrgs, { org_id: "", org_name: "" }]);
  const removeOrg = (i) => setAicloOrgs(aicloOrgs.filter((_, idx) => idx !== i));
  const updateOrg = (i, field, val) => {
    const updated = [...aicloOrgs];
    updated[i] = { ...updated[i], [field]: val };
    setAicloOrgs(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${client.client_id}/csm`, {
        contacts,
        onboarding_date: onboardingDate,
        client_status: clientStatus,
        aiclo_orgs: aicloOrgs,
        csm_notes: csmNotes,
        assigned_csm_id: assignedCsm,
      });
      toast.success("Scheda cliente aggiornata");
      onSave();
      onClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
    setSaving(false);
  };

  if (!client) return null;

  const health = client.health || {};
  const fascia = FASCIA_COLORS[health.fascia] || FASCIA_COLORS.none;
  const historyData = healthData?.history?.slice().reverse() || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="csm-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{client.name}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[clientStatus]}`}>
              {STATUS_LABELS[clientStatus]}
            </span>
            <HealthBadge score={health.score} fascia={health.fascia} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Health Score Section */}
          {health.score !== null && health.score !== undefined && (
            <div className={`p-4 rounded-lg ${fascia.light} ${fascia.border} border`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Heart className={`h-4 w-4 ${fascia.text}`} />
                  <span className={`text-sm font-semibold ${fascia.text}`}>Health Score: {health.score}/100 ({fascia.label})</span>
                </div>
              </div>
              {health.signals && Object.keys(health.signals).length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(health.signals).map(([key, sig]) => {
                    const Icon = SIGNAL_ICONS[key] || AlertTriangle;
                    return (
                      <div key={key} className={`flex items-center gap-2 text-xs p-2 rounded ${sig.active ? "bg-red-100 text-red-700" : "bg-white/60 text-muted-foreground"}`}>
                        <Icon className="h-3.5 w-3.5" />
                        <span>{key.replace(/_/g, " ")}</span>
                        {sig.active && <span className="ml-auto font-semibold">-{sig.penalty}pt</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              {historyData.length > 1 && (
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground mb-1">Storico</p>
                  <ResponsiveContainer width="100%" height={80}>
                    <AreaChart data={historyData}>
                      <defs>
                        <linearGradient id="hGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={false} />
                      <YAxis domain={[0, 100]} tick={false} width={0} />
                      <Tooltip formatter={(v) => `${v}/100`} labelFormatter={() => ""} />
                      <Area type="monotone" dataKey="score" stroke="#10b981" fill="url(#hGrad)" strokeWidth={1.5} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase text-muted-foreground">Timeline Cliente</Label>
            <ClientTimeline clientId={client.client_id} />
          </div>

          <Separator />

          {/* Status & Onboarding */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">CSM assegnato</Label>
              <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={assignedCsm} onChange={(e) => setAssignedCsm(e.target.value)} data-testid="csm-assigned-select">
                <option value="">— Nessuno (tutti) —</option>
                {csmUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name ? `${u.name} (${u.email})` : u.email}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Stato Cliente</Label>
              <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={clientStatus} onChange={(e) => setClientStatus(e.target.value)} data-testid="csm-client-status">
                <option value="onboarding">Onboarding</option>
                <option value="active">Attivo</option>
                <option value="at_risk">A Rischio</option>
                <option value="churn">Churn</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data Onboarding</Label>
              <Input type="date" className="h-9 text-sm" value={onboardingDate} onChange={(e) => setOnboardingDate(e.target.value)} data-testid="csm-onboarding-date" />
            </div>
          </div>

          {/* Contacts (Referenti) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Referenti</Label>
              <Button size="sm" variant="outline" className="h-6 text-xs gap-1" onClick={addContact} data-testid="csm-add-contact">
                <Plus className="h-3 w-3" /> Aggiungi
              </Button>
            </div>
            {contacts.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-2">Nessun referente aggiunto</p>
            ) : (
              <div className="space-y-2">
                {contacts.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input className="h-8 text-xs flex-1" placeholder="Nome" value={c.name} onChange={(e) => updateContact(i, "name", e.target.value)} data-testid={`csm-contact-name-${i}`} />
                    <Input className="h-8 text-xs w-[100px]" placeholder="Ruolo" value={c.role} onChange={(e) => updateContact(i, "role", e.target.value)} data-testid={`csm-contact-role-${i}`} />
                    <Input className="h-8 text-xs flex-1" placeholder="Email" value={c.email} onChange={(e) => updateContact(i, "email", e.target.value)} data-testid={`csm-contact-email-${i}`} />
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600 shrink-0" onClick={() => removeContact(i)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Aiclo Organizations */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Organizzazioni Aiclo</Label>
              <Button size="sm" variant="outline" className="h-6 text-xs gap-1" onClick={addOrg} data-testid="csm-add-org">
                <Link2 className="h-3 w-3" /> Collega
              </Button>
            </div>
            {aicloOrgs.length === 0 ? (
              <div className="text-xs text-muted-foreground italic py-3 text-center bg-muted/30 rounded-lg">
                <WifiOff className="h-4 w-4 mx-auto mb-1 text-muted-foreground/50" />
                Nessuna organizzazione Aiclo collegata
              </div>
            ) : (
              <div className="space-y-2">
                {aicloOrgs.map((org, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input className="h-8 text-xs flex-1" placeholder="Nome Organizzazione" value={org.org_name} onChange={(e) => updateOrg(i, "org_name", e.target.value)} data-testid={`csm-org-name-${i}`} />
                    <Input className="h-8 text-xs w-[140px]" placeholder="ID Organizzazione" value={org.org_id} onChange={(e) => updateOrg(i, "org_id", e.target.value)} data-testid={`csm-org-id-${i}`} />
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600 shrink-0" onClick={() => removeOrg(i)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Notes */}
          <div className="space-y-1">
            <Label className="text-xs">Note generali</Label>
            <textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={csmNotes} onChange={(e) => setCsmNotes(e.target.value)} placeholder="Note interne sul cliente..." data-testid="csm-notes" />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="csm-save-btn">
              <Save className="h-4 w-4" />
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
            <Button variant="outline" onClick={() => onClose(false)}>Annulla</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============== MCP SETTINGS + WIZARD ===============
const MCPSettings = ({ clients, onClientsUpdated }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [showTools, setShowTools] = useState(false);
  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0); // 0=orgs list, 2=done
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [orgMappings, setOrgMappings] = useState({}); // org_id -> client_id
  const [linking, setLinking] = useState(false);

  const loadStatus = useCallback(() => axios.get(`${API}/csm/mcp/status`).then(r => setStatus(r.data)).catch(() => toast.error("Errore stato MCP")), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const connect = async () => {
    setBusy("connect");
    try {
      const r = await axios.get(`${API}/csm/mcp/connect`);
      window.location.href = r.data.auth_url;
      return;
    } catch (err) { toast.error(err.response?.data?.detail || "Collegamento non riuscito"); }
    setBusy("");
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare Aiclo?")) return;
    await axios.post(`${API}/csm/mcp/disconnect`).catch(() => {});
    loadStatus();
  };

  const refreshTools = async () => {
    setBusy("tools");
    try { await axios.post(`${API}/csm/mcp/refresh-tools`); await loadStatus(); toast.success("Strumenti aggiornati"); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setBusy("");
  };

  const testConnection = async () => {
    setBusy("orgs");
    try {
      const res = await axios.post(`${API}/csm/mcp-config/test`);
      const orgs = res.data.organizations || [];
      setAvailableOrgs(orgs);
      toast.success(res.data.message);
      if (orgs.length > 0) {
        setOrgMappings({});
        setWizardStep(0);
        setWizardOpen(true);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Connessione fallita"); }
    setBusy("");
  };

  const setMapping = (orgId, clientId) => {
    setOrgMappings(prev => ({ ...prev, [orgId]: clientId }));
  };

  const linkAll = async () => {
    const links = Object.entries(orgMappings)
      .filter(([_, cid]) => cid)
      .map(([orgId, clientId]) => {
        const org = availableOrgs.find(o => o.org_id === orgId);
        return { client_id: clientId, org_id: orgId, org_name: org?.org_name || "" };
      });
    if (links.length === 0) { toast.error("Seleziona almeno un collegamento"); return; }
    setLinking(true);
    try {
      const res = await axios.post(`${API}/csm/link-organizations`, { links });
      toast.success(`${res.data.linked} organizzazioni collegate!`);
      setWizardStep(2);
      if (onClientsUpdated) onClientsUpdated();
    } catch (err) { toast.error("Errore nel collegamento"); }
    setLinking(false);
  };

  const alreadyLinked = (orgId) => clients.some(c => (c.aiclo_orgs || []).some(o => o.org_id === orgId));
  const linkedClient = (orgId) => {
    const cl = clients.find(c => (c.aiclo_orgs || []).some(o => o.org_id === orgId));
    return cl ? cl.name : null;
  };

  if (!status) return <div className="py-4 flex justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Wifi className="h-4 w-4" />
        <h3 className="text-sm font-semibold">Aiclo MCP</h3>
        <span className="text-xs text-muted-foreground font-mono">{status.mcp_url}</span>
        {status.connected ? (
          <span className="flex items-center gap-1 text-xs text-emerald-600" data-testid="mcp-status-connected"><CheckCircle2 className="h-3.5 w-3.5" /> Collegato</span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="mcp-status-disconnected"><WifiOff className="h-3.5 w-3.5" /> Non collegato</span>
        )}
      </div>
      {!status.connected ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Accedi con il tuo account Aiclo: si apre la pagina di login di Aiclo, autorizzi il CRM e torni qui già collegato.</p>
          <Button className="h-9 text-sm gap-1" onClick={connect} disabled={busy === "connect"} data-testid="mcp-connect-btn">
            <Link2 className="h-3.5 w-3.5" /> {busy === "connect" ? "Apertura..." : "Connetti Aiclo"}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button className="h-9 text-sm gap-1" onClick={testConnection} disabled={busy === "orgs"} data-testid="mcp-test-btn">
            <Link2 className={`h-3.5 w-3.5 ${busy === "orgs" ? "animate-pulse" : ""}`} /> {busy === "orgs" ? "Lettura organizzazioni..." : "Gestisci Organizzazioni"}
          </Button>
          <Button variant="outline" className="h-9 text-sm gap-1" onClick={refreshTools} disabled={busy === "tools"} data-testid="mcp-refresh-tools-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${busy === "tools" ? "animate-spin" : ""}`} /> Aggiorna strumenti
          </Button>
          <Button variant="ghost" className="h-9 text-sm text-muted-foreground" onClick={disconnect} data-testid="mcp-disconnect-btn">Scollega</Button>
        </div>
      )}
      {status.last_error && <p className="text-xs text-red-600" data-testid="mcp-last-error">{status.last_error}</p>}
      {status.last_sync && <p className="text-xs text-muted-foreground">Ultima sync: {new Date(status.last_sync).toLocaleString("it-IT")}</p>}
      {status.connected && (
        <div>
          <button className="text-xs text-primary hover:underline" onClick={() => setShowTools(!showTools)} data-testid="mcp-tools-toggle">
            {showTools ? "Nascondi" : "Mostra"} strumenti disponibili ({status.tools.length})
          </button>
          {showTools && (
            <div className="mt-2 space-y-1 max-h-60 overflow-y-auto" data-testid="mcp-tools-list">
              {status.tools.map(t => (
                <div key={t.name} className="text-xs p-2 rounded bg-muted/40">
                  <span className="font-mono font-semibold">{t.name}</span>
                  {t.description && <p className="text-muted-foreground mt-0.5">{t.description}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Wizard Dialog */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="mcp-wizard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-emerald-600" />
              {wizardStep === 2 ? "Collegamento Completato!" : "Collega Organizzazioni Aiclo"}
            </DialogTitle>
          </DialogHeader>

          {wizardStep === 0 && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Trovate <strong>{availableOrgs.length}</strong> organizzazioni dal tuo MCP. Associa ciascuna a un cliente esistente.
              </p>

              <div className="space-y-3" data-testid="wizard-org-list">
                {availableOrgs.map(org => {
                  const linked = alreadyLinked(org.org_id);
                  const existingClient = linkedClient(org.org_id);
                  return (
                    <div key={org.org_id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${linked ? "border-emerald-200 bg-emerald-50" : orgMappings[org.org_id] ? "border-blue-200 bg-blue-50" : "border-border/60 bg-muted/20"}`} data-testid={`wizard-org-${org.org_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{org.org_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{org.org_id}</p>
                      </div>
                      {linked ? (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 shrink-0">
                          <CheckCircle2 className="h-3.5 w-3.5" /> {existingClient}
                        </span>
                      ) : (
                        <select
                          className="h-8 rounded-md border border-input bg-background px-2 text-xs min-w-[180px]"
                          value={orgMappings[org.org_id] || ""}
                          onChange={(e) => setMapping(org.org_id, e.target.value)}
                          data-testid={`wizard-select-${org.org_id}`}
                        >
                          <option value="">— Seleziona cliente —</option>
                          {clients.map(c => (
                            <option key={c.client_id} value={c.client_id}>{c.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={linkAll} disabled={linking || Object.values(orgMappings).filter(Boolean).length === 0} className="gap-2" data-testid="wizard-link-btn">
                  <Link2 className="h-4 w-4" />
                  {linking ? "Collegamento..." : `Collega (${Object.values(orgMappings).filter(Boolean).length})`}
                </Button>
                <Button variant="outline" onClick={() => setWizardOpen(false)}>Chiudi</Button>
              </div>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="space-y-4 pt-2 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <p className="text-sm text-muted-foreground">
                Le organizzazioni sono state collegate ai clienti. L'Health Score si calcolerà alla prossima sincronizzazione.
              </p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => { setWizardOpen(false); }} data-testid="wizard-done-btn">Chiudi</Button>
                <Button variant="outline" onClick={() => { setWizardStep(0); }} data-testid="wizard-more-btn">
                  Collega Altre
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =============== SIGNAL SETTINGS ===============
const SignalSettings = () => {
  const [signals, setSignals] = useState([]);
  const [fascia, setFascia] = useState({ green_min: 70, yellow_min: 40 });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/csm/signal-settings`).then(res => {
      setSignals(res.data.signals || []);
      setFascia(res.data.fascia || { green_min: 70, yellow_min: 40 });
      setLoaded(true);
    }).catch(() => toast.error("Errore caricamento impostazioni"));
  }, []);

  const updateSignal = (i, field, val) => {
    const updated = [...signals];
    updated[i] = { ...updated[i], [field]: val };
    setSignals(updated);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/csm/signal-settings`, signals.map(s => ({
          signal_id: s.signal_id, weight: s.weight, threshold: s.threshold, enabled: s.enabled
        }))),
        axios.put(`${API}/csm/fascia-settings`, fascia),
      ]);
      toast.success("Impostazioni salvate, Health Score ricalcolati");
    } catch (err) { toast.error("Errore nel salvataggio"); }
    setSaving(false);
  };

  if (!loaded) return <div className="py-6 flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Segnali di Criticità
        </h3>
        <div className="space-y-3">
          {signals.map((s, i) => {
            const Icon = SIGNAL_ICONS[s.signal_id] || AlertTriangle;
            return (
              <div key={s.signal_id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-muted-foreground">Peso</Label>
                    <Input type="number" className="h-7 w-16 text-xs text-center" value={s.weight} onChange={(e) => updateSignal(i, "weight", parseInt(e.target.value) || 0)} data-testid={`signal-weight-${s.signal_id}`} />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-muted-foreground">Soglia</Label>
                    <Input type="number" className="h-7 w-16 text-xs text-center" value={s.threshold} onChange={(e) => updateSignal(i, "threshold", parseFloat(e.target.value) || 0)} data-testid={`signal-threshold-${s.signal_id}`} />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-muted-foreground">Attivo</Label>
                    <button
                      className={`h-7 w-12 rounded-full flex items-center px-1 transition-colors ${s.enabled ? "bg-emerald-500" : "bg-gray-300"}`}
                      onClick={() => updateSignal(i, "enabled", !s.enabled)}
                      data-testid={`signal-toggle-${s.signal_id}`}
                    >
                      <div className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${s.enabled ? "translate-x-5" : ""}`} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Separator />

      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Heart className="h-4 w-4" /> Fasce Health Score
        </h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <Label className="text-xs">Verde da</Label>
            <Input type="number" className="h-7 w-16 text-xs text-center" value={fascia.green_min} onChange={(e) => setFascia({ ...fascia, green_min: parseInt(e.target.value) || 0 })} data-testid="fascia-green-min" />
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <Label className="text-xs">Giallo da</Label>
            <Input type="number" className="h-7 w-16 text-xs text-center" value={fascia.yellow_min} onChange={(e) => setFascia({ ...fascia, yellow_min: parseInt(e.target.value) || 0 })} data-testid="fascia-yellow-min" />
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <Label className="text-xs">Rosso sotto {fascia.yellow_min}</Label>
          </div>
        </div>
      </div>

      <Button onClick={saveAll} disabled={saving} className="gap-2" data-testid="csm-settings-save">
        <Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva Impostazioni"}
      </Button>
    </div>
  );
};

// =============== VISTA OGGI ===============
const VistaOggi = ({ clients }) => {
  const [expiring, setExpiring] = useState([]);
  const [loadingExp, setLoadingExp] = useState(true);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/contracts/expiring?days=30`).then(res => {
      setExpiring(res.data.contracts || []);
    }).catch(() => {}).finally(() => setLoadingExp(false));
    axios.get(`${API}/csm/tasks`).then(res => setTasks(res.data.tasks || [])).catch(() => {});
  }, []);
  const urgentTasks = tasks.filter(t => isOverdue(t) || isDueSoon(t));

  // Clients that changed fascia (fascia_changed flag from last sync)
  const fasciaChanged = clients.filter(c => c.health?.fascia_changed);

  // Clients without recent contact (no contacts and no csm_notes, or onboarding with no contacts)
  const noContact = clients.filter(c => {
    const hasContacts = (c.contacts || []).length > 0;
    const hasNotes = !!c.csm_notes;
    const isAtRisk = c.client_status === "at_risk" || c.client_status === "churn";
    // Flag: no contacts at all, or at_risk/churn without notes
    return !hasContacts || (isAtRisk && !hasNotes);
  });

  // Clients in onboarding status (need attention)
  const onboarding = clients.filter(c => c.client_status === "onboarding");

  const today = new Date().toLocaleDateString("it-IT", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" data-testid="vista-oggi-title">Vista Oggi</h2>
          <p className="text-sm text-muted-foreground capitalize">{today}</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className={`border-border/60 ${expiring.length > 0 ? "border-orange-200 bg-orange-50" : ""}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <CalendarClock className={`h-4 w-4 ${expiring.length > 0 ? "text-orange-600" : "text-muted-foreground"}`} />
              <p className="text-xs font-medium uppercase text-muted-foreground">In Scadenza (30gg)</p>
            </div>
            <p className={`text-2xl font-bold ${expiring.length > 0 ? "text-orange-700" : ""}`}>{loadingExp ? "..." : expiring.length}</p>
          </CardContent>
        </Card>
        <Card className={`border-border/60 ${fasciaChanged.length > 0 ? "border-red-200 bg-red-50" : ""}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Bell className={`h-4 w-4 ${fasciaChanged.length > 0 ? "text-red-600" : "text-muted-foreground"}`} />
              <p className="text-xs font-medium uppercase text-muted-foreground">Fascia Cambiata</p>
            </div>
            <p className={`text-2xl font-bold ${fasciaChanged.length > 0 ? "text-red-700" : ""}`}>{fasciaChanged.length}</p>
          </CardContent>
        </Card>
        <Card className={`border-border/60 ${noContact.length > 0 ? "border-amber-200 bg-amber-50" : ""}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className={`h-4 w-4 ${noContact.length > 0 ? "text-amber-600" : "text-muted-foreground"}`} />
              <p className="text-xs font-medium uppercase text-muted-foreground">Da Contattare</p>
            </div>
            <p className={`text-2xl font-bold ${noContact.length > 0 ? "text-amber-700" : ""}`}>{noContact.length}</p>
          </CardContent>
        </Card>
        <Card className={`border-border/60 ${onboarding.length > 0 ? "border-blue-200 bg-blue-50" : ""}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className={`h-4 w-4 ${onboarding.length > 0 ? "text-blue-600" : "text-muted-foreground"}`} />
              <p className="text-xs font-medium uppercase text-muted-foreground">In Onboarding</p>
            </div>
            <p className={`text-2xl font-bold ${onboarding.length > 0 ? "text-blue-700" : ""}`}>{onboarding.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tasks overdue / due soon */}
      <Card className={`border-border/60 ${urgentTasks.some(isOverdue) ? "border-red-200" : ""}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ListTodo className="h-4 w-4 text-violet-500" />
            Task Scaduti e in Scadenza (3 giorni)
            <span className="ml-1 text-xs font-normal text-muted-foreground" data-testid="today-tasks-count">{urgentTasks.length}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {urgentTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Nessun task scaduto o in scadenza</p>
          ) : (
            <div className="space-y-2" data-testid="today-tasks-list">
              {urgentTasks.map(t => (
                <div key={t.task_id} className={`flex items-center justify-between p-3 rounded-lg ${isOverdue(t) ? "bg-red-50" : "bg-muted/30"}`} data-testid={`today-task-${t.task_id}`}>
                  <div>
                    <p className="text-sm font-medium">{t.title}</p>
                    <p className="text-xs text-muted-foreground">{[t.client_name, t.assignee_name].filter(Boolean).join(" · ") || "—"}</p>
                  </div>
                  <span className={`text-xs font-semibold ${isOverdue(t) ? "text-red-600" : "text-orange-600"}`}>
                    {isOverdue(t) ? "Scaduto " : "Scade "}{new Date(t.due_date).toLocaleDateString("it-IT")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expiring Contracts */}
      <Card className="border-border/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-orange-500" />
            Contratti in Scadenza (prossimi 30 giorni)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingExp ? (
            <div className="flex items-center justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
          ) : expiring.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Nessun contratto in scadenza nei prossimi 30 giorni</p>
          ) : (
            <div className="space-y-2" data-testid="expiring-contracts-list">
              {expiring.map(c => {
                const daysLeft = Math.ceil((new Date(c.expiration_date) - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <div key={c.contract_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`expiring-${c.contract_id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${daysLeft <= 7 ? "bg-red-500" : daysLeft <= 14 ? "bg-orange-500" : "bg-amber-400"}`} />
                      <div>
                        <p className="text-sm font-medium">{c.client_name}</p>
                        <p className="text-xs text-muted-foreground">{c.sales_rep_name} &middot; {c.sale_type}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{new Date(c.expiration_date).toLocaleDateString("it-IT")}</p>
                      <p className={`text-xs font-medium ${daysLeft <= 7 ? "text-red-600" : daysLeft <= 14 ? "text-orange-600" : "text-amber-600"}`}>
                        {daysLeft === 0 ? "Oggi" : daysLeft === 1 ? "Domani" : `${daysLeft} giorni`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fascia Changed */}
      {fasciaChanged.length > 0 && (
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-4 w-4 text-red-500" />
              Clienti con Fascia Cambiata
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="fascia-changed-list">
              {fasciaChanged.map(c => (
                <div key={c.client_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <HealthBadge score={c.health?.score} fascia={c.health?.fascia} />
                    <span className="text-sm font-medium">{c.name}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[c.client_status || "active"]}`}>
                    {STATUS_LABELS[c.client_status || "active"]}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Contact */}
      {noContact.length > 0 && (
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-amber-500" />
              Account Senza Contatto / Da Gestire
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="no-contact-list">
              {noContact.slice(0, 15).map(c => (
                <div key={c.client_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${c.client_status === "churn" ? "bg-red-500" : c.client_status === "at_risk" ? "bg-orange-500" : "bg-amber-400"}`} />
                    <div>
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.sector} &middot; {(c.contacts || []).length === 0 ? "Nessun referente" : "Note mancanti"}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[c.client_status || "active"]}`}>
                    {STATUS_LABELS[c.client_status || "active"]}
                  </span>
                </div>
              ))}
              {noContact.length > 15 && (
                <p className="text-xs text-muted-foreground text-center py-1">e altri {noContact.length - 15} clienti...</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Onboarding */}
      {onboarding.length > 0 && (
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              Clienti in Onboarding
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="onboarding-list">
              {onboarding.map(c => (
                <div key={c.client_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <div>
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.sector} &middot; {c.onboarding_date ? `Dal ${new Date(c.onboarding_date).toLocaleDateString("it-IT")}` : "Data onboarding non impostata"}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// =============== MAIN CSM PAGE ===============
export default function CSMPage({ focusClient }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("oggi");
  const [selectedClient, setSelectedClient] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => {
    const c = focusClient && clients.find(x => x.client_id === focusClient.id);
    if (c) { setSelectedClient(c); setDialogOpen(true); }
  }, [focusClient, clients]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fasciaFilter, setFasciaFilter] = useState("");
  const [syncing, setSyncing] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/csm/clients`);
      setClients(res.data.clients || []);
    } catch (err) {
      toast.error("Errore nel caricamento");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const a = params.get("aiclo");
    if (a) {
      setActiveView("settings");
      if (a === "connected") toast.success("Aiclo collegato!");
      else toast.error("Collegamento Aiclo non riuscito");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    const g = params.get("gmail");
    if (!g) return;
    setActiveView("email");
    if (g === "connected") toast.success("Gmail collegato!");
    else toast.error("Collegamento Gmail non riuscito");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/csm/sync`);
      toast.success(`Sync completata: ${res.data.synced_clients} clienti aggiornati`);
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore sync");
    }
    setSyncing(false);
  };

  const filtered = clients.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!c.name.toLowerCase().includes(q) && !(c.sector || "").toLowerCase().includes(q)) return false;
    }
    if (statusFilter && (c.client_status || "active") !== statusFilter) return false;
    if (fasciaFilter && (c.health?.fascia || "none") !== fasciaFilter) return false;
    return true;
  });

  // Stats
  const stats = {
    total: clients.length,
    at_risk: clients.filter(c => (c.client_status || "") === "at_risk").length,
    churn: clients.filter(c => (c.client_status || "") === "churn").length,
    connected: clients.filter(c => c.aiclo_orgs?.length > 0).length,
    green: clients.filter(c => c.health?.fascia === "green").length,
    yellow: clients.filter(c => c.health?.fascia === "yellow").length,
    red: clients.filter(c => c.health?.fascia === "red").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="csm-module">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex bg-muted rounded-md p-0.5" data-testid="csm-view-tabs">
            <button onClick={() => setActiveView("oggi")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "oggi" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="csm-tab-oggi">
              <CalendarClock className="h-3.5 w-3.5" /> Oggi
            </button>
            <button onClick={() => setActiveView("clients")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "clients" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="csm-tab-clients">
              <Users className="h-3.5 w-3.5" /> Clienti
            </button>
            <button onClick={() => setActiveView("email")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "email" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="csm-tab-email">
              <Mail className="h-3.5 w-3.5" /> Email
            </button>
            <button onClick={() => setActiveView("tasks")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "tasks" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="csm-tab-tasks">
              <ListTodo className="h-3.5 w-3.5" /> Task
            </button>
            <button onClick={() => setActiveView("settings")} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-all ${activeView === "settings" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="csm-tab-settings">
              <Settings className="h-3.5 w-3.5" /> Configurazione
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(activeView === "clients" || activeView === "oggi") && (
            <Button variant="outline" className="h-9 gap-2 text-sm" onClick={triggerSync} disabled={syncing} data-testid="csm-sync-btn">
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizzando..." : "Aggiorna Ora"}
            </Button>
          )}
        </div>
      </div>

      {activeView === "oggi" && (
        <VistaOggi clients={clients} />
      )}

      {activeView === "email" && <CSMEmails clients={clients} />}

      {activeView === "tasks" && <TasksView clients={clients} />}

      {activeView === "clients" && (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Card className="border-border/60">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground uppercase">Totale</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="border-border/60">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground uppercase">Collegati Aiclo</p>
                <p className="text-xl font-bold">{stats.connected}<span className="text-sm font-normal text-muted-foreground">/{stats.total}</span></p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-3 cursor-pointer" onClick={() => setFasciaFilter(fasciaFilter === "green" ? "" : "green")}>
                <p className="text-xs text-emerald-600 uppercase">Verde</p>
                <p className="text-xl font-bold text-emerald-700">{stats.green}</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-3 cursor-pointer" onClick={() => setFasciaFilter(fasciaFilter === "yellow" ? "" : "yellow")}>
                <p className="text-xs text-amber-600 uppercase">Giallo</p>
                <p className="text-xl font-bold text-amber-700">{stats.yellow}</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-3 cursor-pointer" onClick={() => setFasciaFilter(fasciaFilter === "red" ? "" : "red")}>
                <p className="text-xs text-red-600 uppercase">Rosso</p>
                <p className="text-xl font-bold text-red-700">{stats.red}</p>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-3">
                <p className="text-xs text-orange-600 uppercase">A Rischio + Churn</p>
                <p className="text-xl font-bold text-orange-700">{stats.at_risk + stats.churn}</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="h-9 pl-9 text-sm" placeholder="Cerca cliente..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="csm-search" />
            </div>
            <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="csm-filter-status">
              <option value="">Tutti gli stati</option>
              <option value="onboarding">Onboarding</option>
              <option value="active">Attivo</option>
              <option value="at_risk">A Rischio</option>
              <option value="churn">Churn</option>
            </select>
            <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={fasciaFilter} onChange={(e) => setFasciaFilter(e.target.value)} data-testid="csm-filter-fascia">
              <option value="">Tutte le fasce</option>
              <option value="green">Verde</option>
              <option value="yellow">Giallo</option>
              <option value="red">Rosso</option>
              <option value="none">Non collegato</option>
            </select>
            {(statusFilter || fasciaFilter || search) && (
              <Button variant="ghost" size="sm" className="text-xs h-9" onClick={() => { setStatusFilter(""); setFasciaFilter(""); setSearch(""); }}>
                Reset
              </Button>
            )}
          </div>

          {/* Client List */}
          <div className="rounded-lg border border-border/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">CLIENTE</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">STATO</th>
                  <th className="px-3 py-2.5 text-center text-xs font-medium text-muted-foreground">HEALTH</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">CSM</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">SETTORE</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">REFERENTI</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">AICLO</th>
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">ONBOARDING</th>
                  <th className="px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const health = c.health || {};
                  const status = c.client_status || "active";
                  const orgCount = (c.aiclo_orgs || []).length;
                  const contactCount = (c.contacts || []).length;
                  return (
                    <tr key={c.client_id} className="border-t border-border/40 hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => { setSelectedClient(c); setDialogOpen(true); }} data-testid={`csm-client-row-${c.client_id}`}>
                      <td className="px-4 py-3">
                        <span className="font-medium">{c.name}</span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[status]}`}>
                          {STATUS_LABELS[status]}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex justify-center">
                          <HealthBadge score={health.score} fascia={health.fascia} />
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs" data-testid={`csm-client-assigned-${c.client_id}`}>{c.assigned_csm_name || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-3 text-muted-foreground">{c.sector}</td>
                      <td className="px-3 py-3">
                        {contactCount > 0 ? (
                          <span className="text-xs">{contactCount} referent{contactCount === 1 ? "e" : "i"}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {orgCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><Wifi className="h-3 w-3" /> {orgCount}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground"><WifiOff className="h-3 w-3 inline" /></span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">
                        {c.onboarding_date ? new Date(c.onboarding_date).toLocaleDateString("it-IT") : "—"}
                      </td>
                      <td className="px-2 py-3">
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-2 bg-muted/30 border-t border-border/40 text-xs text-muted-foreground">
              {filtered.length} client{filtered.length === 1 ? "e" : "i"}{(statusFilter || fasciaFilter || search) ? " (filtrati)" : ""}
            </div>
          </div>
        </>
      )}

      {activeView === "settings" && (
        <div className="max-w-2xl space-y-6">
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Connessione MCP</CardTitle>
            </CardHeader>
            <CardContent>
              <MCPSettings clients={clients} onClientsUpdated={loadClients} />
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Segnali & Health Score</CardTitle>
            </CardHeader>
            <CardContent>
              <SignalSettings />
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Riepiloghi Email</CardTitle>
            </CardHeader>
            <CardContent>
              <DigestSettings />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Detail Dialog */}
      <ClientDetailDialog
        open={dialogOpen}
        onClose={(v) => { setDialogOpen(v); if (!v) setSelectedClient(null); }}
        client={selectedClient}
        onSave={loadClients}
      />
    </div>
  );
}
